"use client";

import type { JSX } from "react";
import { cn } from "@repo/ui/utils";
import { LinkButton } from "@repo/ui/link-button";
import { IconButton } from "@repo/ui/icon-button";
import { useCardCarousel } from "@repo/ui/card-carousel";
import { ActionLink } from "@/components/common/actions/actions.component";
import { getI18nString } from "@/lib/utils";
import type { HeaderInfoCardData } from "./header-info-card.types";

interface HeaderInfoCardControlsProps {
  action: HeaderInfoCardData["action"];
  descriptionId: string;
}

export function HeaderInfoCardControls({
  action,
  descriptionId,
}: HeaderInfoCardControlsProps): JSX.Element | null {
  const carousel = useCardCarousel();
  const showCarouselControls = Boolean(
    carousel && (carousel.slidesCount ?? 0) > 1,
  );

  if (!showCarouselControls && !action?.to) return null;

  return (
    <div
      className={cn(
        "mt-auto flex items-center gap-4",
        showCarouselControls ? "justify-between" : "justify-end",
      )}
    >
      {showCarouselControls && carousel ? (
        <div className="flex items-center gap-2">
          <IconButton
            icon="arrow-left"
            variant="blue"
            size="sm"
            aria-label="Previous card"
            onClick={carousel.scrollPrev}
            data-testid="header-info-card-prev"
          />
          <IconButton
            icon="arrow-right"
            variant="blue"
            size="sm"
            aria-label="Next card"
            onClick={carousel.scrollNext}
            data-testid="header-info-card-next"
          />
        </div>
      ) : null}
      {action?.to ? (
        <ActionLink
          actionTo={action.to}
          aria-labelledby={descriptionId}
          className="leading-none"
        >
          <LinkButton asChild underline variant="primary" size="md">
            <span>{getI18nString(action.text, "Learn more")}</span>
          </LinkButton>
        </ActionLink>
      ) : null}
    </div>
  );
}
